import { useEffect, useState } from 'react';

import {
  fetchHealthConditions,
  fetchUserHealthConditions,
  type HealthCondition,
} from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';

import { selectWhitelistedConditions } from '../utils/selectWhitelistedConditions';

type UseHealthConditions = {
  /** Catalog of selectable conditions, sorted by display name. */
  conditions: HealthCondition[];
  /** The user's already-saved condition keys, for prefill. */
  initialSelection: string[];
  isLoading: boolean;
  error: unknown;
};

// Fetches the conditions catalog + the user's current selection on mount and
// holds them in local state — the catalog is screen-scoped, so (like
// useLegalDocuments) it doesn't belong in RulesEngineProvider. The catalog is
// filtered against the client-side whitelist (mirroring Sparta/stylos) so keys
// the survey shouldn't surface — e.g. `overweight` / `obesity` — are dropped,
// then sorted by display name to match stylos's alphabetical ordering.
export function useHealthConditions(): UseHealthConditions {
  const { user } = useRulesEngine();
  const userUrn = user?.urn;

  const [conditions, setConditions] = useState<HealthCondition[]>([]);
  const [initialSelection, setInitialSelection] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      fetchHealthConditions(),
      userUrn ? fetchUserHealthConditions(userUrn) : Promise.resolve<string[]>([]),
    ])
      .then(([catalog, selected]) => {
        if (cancelled) return;
        setConditions(selectWhitelistedConditions(catalog));
        setInitialSelection(selected);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userUrn]);

  return { conditions, initialSelection, isLoading, error };
}
